// Module / action definitions for the role-based permission system.
//
// Keep `key` values in sync with backend/src/permissions.js (MODULES). Each
// non-admin user's `permissions` object is keyed by these module keys, and each
// module holds the four action booleans below.
//
//   permissions = {
//     domains: { view, add, edit, delete },
//     ...
//   }

// `navTo` links a module to its sidebar/route path so we can gate navigation by
// the module's `view` action. `null` = not a standalone page.
export const PERMISSION_MODULES = [
  { key: "domains", label: "Domains", navTo: "/domains" },
  { key: "servers", label: "Servers", navTo: "/servers" },
  { key: "clients", label: "Clients", navTo: "/clients" },
  { key: "bankAccounts", label: "Bank Accounts", navTo: "/bank-accounts" },
  { key: "monitors", label: "Domain Monitors", navTo: "/domain-monitors" },
  { key: "categories", label: "Category", navTo: "/category" },
  { key: "alerts", label: "Expiry Alerts", navTo: "/expiry-alerts" },
  { key: "logs", label: "Activity Log", navTo: "/activity-log" },
  { key: "sessions", label: "Sessions", navTo: "/sessions" },
  { key: "invoices", label: "Invoices", navTo: "/invoices" },
  { key: "locations", label: "Locations", navTo: "/locations" },
  { key: "attendance", label: "Attendance", navTo: "/attendance" },
];

export const PERMISSION_ACTIONS = [
  { key: "view", label: "View" },
  { key: "add", label: "Add" },
  { key: "edit", label: "Edit" },
  { key: "delete", label: "Delete" },
];

// A blank permission map (everything denied) — used as the starting point when
// an admin creates a new "user" or opens one whose permissions are still NULL.
export function emptyPermissions() {
  const out = {};
  for (const m of PERMISSION_MODULES) {
    out[m.key] = { view: false, add: false, edit: false, delete: false };
  }
  return out;
}

// Normalize a possibly-partial/NULL permissions object into a full grid so the
// editor checkboxes always have a boolean to bind to.
export function normalizePermissions(perms) {
  const base = emptyPermissions();
  if (perms && typeof perms === "object") {
    for (const m of PERMISSION_MODULES) {
      const row = perms[m.key];
      if (row && typeof row === "object") {
        base[m.key] = {
          view: !!row.view,
          add: !!row.add,
          edit: !!row.edit,
          delete: !!row.delete,
        };
      }
    }
  }
  return base;
}

// Core check used by both the AuthContext helper and any direct callers.
// Admins always pass.
export function userCan(user, moduleKey, action) {
  if (!user) return false;
  if (user.role === "admin") return true;
  const perms = user.permissions;
  if (!perms || typeof perms !== "object") return false;
  const row = perms[moduleKey];
  return !!(row && row[action] === true);
}
